import {
    Breakpoints,
    ButtonNext,
    Image,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import {Discount, Info, StarOutline} from '@volkswagen-onehub/icons-core'
import React from 'react'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import blueGradient from '../../../assets/css-vars/blueGradient'
import {gridUtils} from '../../../assets/css-vars/gridUtils'
import TrackingAction from '../../../tracking-manager'
import {useFeatureServices} from '../../../use-feature-services'
import Brands from '../../../utils/brand/brand'
import formatCurrency from '../../../utils/currency/formatCurrency'
import CustomerTypes, {
    CustomerTypesUrl,
} from '../../../utils/customerTypes/customerTypes'
import {
    getVehiclePromotionsLinkClickData,
    getVehiclePromotionsSelectClickData,
} from '../../../utils/tracking/getTrackingData'
import setTracking, {
    checkTrackingManager,
} from '../../../utils/tracking/tracking-utils/setTracking'
import UrlRoutes from '../../../utils/url/urlRoutes'
import {RootState} from '../../rootReducer'
import EyeCatcher from '../ui/EyeCatcher'
import DealerHighlighter from '../ui/DealerHighlighter'
import FuelTags from '../ui/FuelTags'
import {FuelCode} from '../../../utils/filters/FuelsKeys'
import {PromotionType} from '../../../shared/variables'
import {capitalizeWords} from '../../../utils/text/dealer'
import STYlE_CONSTANTS from '../../../utils/style/styleConstants'
import styled from 'styled-components'
import {isPngSrcImage} from '../../../shared/utilities'
import isTooDark from '../../../utils/colors/isTooDark'
import {globalColors} from '../../../utils/style/globalColors'
import {
    PURPLE_EYE_CATCHER_BACKGROUND,
    ULTRAMARINE_EYE_CATCHER_BACKGROUND,
} from '../../../utils/style/eyeCatcherColors'

const ModelFigure = styled.figure`
    position: relative;
    margin: 0;
    padding: 0;
`

const HIGHLIGHTED_CLASS = 'SingleModelHighlighted'
const PROMO_AVAILABLE_CLASS = 'IsPromoAvailable'

// Measurement constants
const HEADER_MIN_HEIGHT_DEFAULT = '87px'
const HEADER_MIN_HEIGHT_WRAPPED = '115px'
const CONTENT_OFFSET = '72px'

const ModelContainerShadow = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100% - ${CONTENT_OFFSET});
    border-radius: ${(props) => props.theme.size.borderRadius.card};
    background: linear-gradient(0deg, rgba(246, 245, 242, 0) 30%, #d0d1d5 100%),
        ${(props) => props.theme.colors.background.primary};
`

const ModelHeader = styled.div<{$isPng?: boolean; $tagsWrapped?: boolean}>`
    display: flex;
    flex-direction: column;
    padding: 15px 15px ${(props) => props.theme.size.static250};
    width: 100%;
    background-color: ${({$isPng}) =>
        $isPng ? globalColors.white000 : 'transparent'};
    border-top-right-radius: ${(props) => props.theme.size.borderRadius.card};
    border-top-left-radius: ${(props) => props.theme.size.borderRadius.card};
    min-height: ${({$tagsWrapped}) =>
        $tagsWrapped ? HEADER_MIN_HEIGHT_WRAPPED : HEADER_MIN_HEIGHT_DEFAULT};
    position: ${({$isPng}) => ($isPng ? 'relative' : 'absolute')};
    z-index: ${({$isPng}) => ($isPng ? 'auto' : '2')};
`

const ModelTitleRow = styled.div<{$abs?: boolean; $hasTags?: boolean}>`
    position: ${({$abs}) => ($abs ? 'absolute' : 'relative')};
    z-index: ${({$abs}) => ($abs ? '1' : '0')};
    display: flex;
    align-items: flex-start;
    gap: ${(props) => props.theme.size.static100};
    width: 100%;
    margin: ${({$hasTags, theme}) =>
        $hasTags
            ? `-${theme.size.static350} 0 ${theme.size.static150}`
            : `0 0 ${theme.size.static150}`};
`

const ModelName = styled(Text)`
    font-size: ${(props) => props.theme.size.static350};
    line-height: 1.1;
    flex: 1 1 auto;
    min-width: 0;
    color: ${(props) => props.theme.colors.content.primary};
    margin: 0;

    &.text-white {
        color: ${(props) => props.theme.colors.content.inverted};
    }

    &.text-black {
        color: ${(props) => props.theme.colors.content.primary};
    }
`

const ModelFuelTags = styled(FuelTags)`
    justify-content: flex-end;
    margin-bottom: auto;
    margin-left: auto;
`

const TitleRowFuelTags = styled(ModelFuelTags)`
    flex: 0 0 auto;
    margin-bottom: 0;
`

const FivePlusLayerCta = styled.button`
    display: inline-flex;
    align-items: center;
    align-self: flex-end;
    gap: ${(props) => props.theme.size.static100};
    margin: auto 0 0 auto;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${(props) => props.theme.colors.content.primary};
    cursor: pointer;
    font: inherit;
    font-weight: ${STYlE_CONSTANTS.TEXT_WEIGHT_BOLD};
`

interface CmsServiceWithLayer {
    openLayer?: (path: string) => Promise<boolean>
}

const ModelTagsContainer = styled.div`
    position: relative;
    top: -28px;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    margin-inline-end: calc(${(props) => props.theme.size.static100} * -1);
    margin-block-end: calc(${(props) => props.theme.size.static100} * -1);
`

const ModelTag = styled.div<{$isPrimary?: boolean}>`
    display: flex;
    gap: ${(props) => props.theme.size.static100};
    -webkit-box-align: center;
    align-items: center;
    width: fit-content;
    max-width: 100%;
    height: ${(props) => props.theme.size.static350};
    white-space: nowrap;
    padding: ${(props) => props.theme.size.static150};
    background-color: ${({$isPrimary, theme}) =>
        $isPrimary
            ? theme.colors.displayTag.recommended.background
            : theme.colors.displayTag.offer.background};
    color: ${({$isPrimary, theme}) =>
        $isPrimary
            ? theme.colors.displayTag.recommended.content
            : theme.colors.displayTag.offer.content};
    border-radius: ${(props) => props.theme.size.borderRadius.displayTag};
    border: none;
    margin-inline-end: ${(props) => props.theme.size.static100};
    margin-block-end: ${(props) => props.theme.size.static100};
    font-size: 0.88rem;
    line-height: 150%;
    letter-spacing: calc(-0.02em + 0.02rem);
`

const ModelImageWithBackgroundContainer = styled.div<{$isPng?: boolean}>`
    position: relative;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    width: 100%;
    min-height: 175px;
    ${({$isPng, theme}) =>
        !$isPng
            ? `
        border-top: ${theme.size.border.default} solid ${theme.colors.border.default};
        border-top-left-radius: ${theme.size.borderRadius.card};
        border-top-right-radius: ${theme.size.borderRadius.card};
        overflow: hidden;
    `
            : ''}
`

const EyeCatcherAccordionContainer = styled.div`
    margin: 0 0 20px;
    &.${PROMO_AVAILABLE_CLASS} {
        margin-bottom: ${(props) => props.theme.size.static500};
    }
`

interface PromoText {
    price_prefix: string
    price: number | string
    price_suffix: string
    promotional_package: string
}

interface ListingCarDetails {
    carName: string
    carImage: string
    backgroundColor: string
    promoText: PromoText
    promoType: string
    promoLink: string
    promoIsHighlighted: boolean
    promotional_equipment: string
    isDealerSelected: boolean
    isPromoAvailable: number
    dealerName: string
    promoScope: string
    tag_label_1?: string | null
    tag_label_2?: string | null
    fuels?: string[]
    tagsWrapped?: boolean
}

const ModelImageContainer = styled.div<{
    $isPng?: boolean
    $tagsWrapped?: boolean
}>`
    position: relative;
    height: ${({$isPng, $tagsWrapped}) =>
        $isPng ? '175px' : $tagsWrapped ? '290px' : '262px'};
    display: flex;
    -webkit-box-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    align-items: center;
    -webkit-box-flex: 1;
    flex-grow: 1;
`

const ModelImage = styled(Image)<{$isPng?: boolean}>`
    position: relative;
    max-width: ${({$isPng}) => ($isPng ? '380px' : 'unset')};
    height: ${({$isPng}) => ($isPng ? 'auto' : '100%')};
    max-height: 100%;
    transition: transform 0.3s ease-in-out;

    &:hover,
    &:focus-within {
        transform: ${({$isPng}) => ($isPng ? 'scale(1.1)' : 'none')};
    }
`

const ContentContainer = styled.div`
    margin-top: 15px;
    padding: 0 15px;

    @media (min-width: ${Breakpoints.b960}px) {
        margin-top: 20px;
    }

    .${HIGHLIGHTED_CLASS} & {
        color: ${(props) => props.theme.colors.content.inverted};
        padding-bottom: ${gridUtils.gap};

        @media (min-width: ${Breakpoints.b560}px) {
            padding-right: ${gridUtils.gap};
            padding-left: ${gridUtils.gap};
        }
    }
`

const ModelTextContainer = styled.div`
    position: relative;

    &.${PROMO_AVAILABLE_CLASS} {
        padding-top: ${(props) => props.theme.size.static300};
    }
`

const ModelPromoType = styled(Text)`
    font-size: ${(props) => props.theme.size.static250};
    line-height: 1.375;
    margin-top: 5px;
    margin-bottom: 20px;

    &.${PROMO_AVAILABLE_CLASS} {
        margin-bottom: ${(props) => props.theme.size.static500};
    }
`

// Not a link on purpose: the CTA inside is the only link of the card (links and
// buttons can't be nested in a link); the card click is just a bigger target for
// mouse users
const SingleModelCard = styled.div`
    cursor: pointer;

    &.${HIGHLIGHTED_CLASS} {
        ${blueGradient};
        color: ${(props) => props.theme.colors.content.inverted};
    }
`

const SingleModelPromoText = styled.p`
    font-size: ${(props) => props.theme.size.static250};
    line-height: 1.375;
    color: ${(props) => props.theme.colors.content.primary};
    font-weight: ${STYlE_CONSTANTS.TEXT_WEIGHT_BOLD};
    margin: 0;
    padding: 0;

    > * {
        display: inline;
    }

    &.text-white {
        color: ${(props) => props.theme.colors.content.inverted} !important;
    }
`

const SingleModelCTA = styled(ButtonNext)`
    // remove margin from text
    * {
        margin: 0;
    }
`

const SingleModelEyeCatcherWrapper = styled.div`
    margin-bottom: 10px;

    @media only screen and (min-width: ${Breakpoints.b1280}px) {
        --half-gap: calc(${gridUtils.gap} / 2);

        transform: translateX(calc(-${gridUtils.gap} - var(--half-gap)));
    }
`

function canPrintPromoText(promoText: PromoText): string | null {
    if (!promoText.price) {
        return null
    }

    const prefix = promoText.price_prefix ? promoText.price_prefix : ''
    const suffix = promoText.price_suffix ? promoText.price_suffix : ''
    const text = `${prefix} ${formatCurrency(promoText.price)} ${suffix}`

    return text.trim()
}

// Only electrified models are flagged in the listing; FuelTags sorts the tags
function getElectrifiedFuels(fuels?: string[]): FuelCode[] {
    if (!fuels || fuels.length === 0) {
        return []
    }

    const electrifiedFuels: FuelCode[] = ['EL', 'PHY', 'FH', 'MHY']

    return electrifiedFuels.filter((fuel) => fuels.includes(fuel))
}

// tslint:disable-next-line:cyclomatic-complexity
export function SingleModel(props: ListingCarDetails): React.JSX.Element {
    const {t} = useTranslation('app')
    const promoText = canPrintPromoText(props.promoText)
    // tslint:disable-next-line:no-any
    const {'s2:history': historyService} = useFeatureServices() as any
    const {tracking: trackingManager} = useFeatureServices()
    const {'cms-service': cmsService} = useFeatureServices() as {
        'cms-service': CmsServiceWithLayer
    }
    const trackingBrand = useSelector(
        (state: RootState) => state.general.trackingBrand
    )
    const isShowroom = useSelector(
        (state: RootState) => state.general.isShowroom
    )
    const customerTypeKey = useSelector(
        (state: RootState) => state.general.customerType
    )
    const brandFromState = useSelector(
        (state: RootState) => state.general.brand
    )
    const fivePlusLayerPathFromState = useSelector(
        (state: RootState) => state.general.fivePlusLayerPath
    )
    const isPngImage = isPngSrcImage(props.carImage)
    const fivePlusLayerPath =
        brandFromState === Brands.VIC ? fivePlusLayerPathFromState.trim() : ''
    const isFivePlusConfigured = Boolean(fivePlusLayerPath)
    const electrifiedFuels = getElectrifiedFuels(props.fuels)
    const fivePlusCtaLabel = t('cta.listing.fivePlusIncluded')
    const hasFuelTags = isPngImage && electrifiedFuels.length > 0

    const getPromoLink = () => {
        if (brandFromState === Brands.VW) {
            return `/${UrlRoutes.SINGLE_CAR}/${props.promoLink}`
        }
        const customerType =
            customerTypeKey === CustomerTypes.PRIVATE
                ? CustomerTypesUrl.PR
                : CustomerTypesUrl.BU

        return `/${UrlRoutes.CUSTOMER_TYPE}/${customerType}/${UrlRoutes.SINGLE_CAR}/${props.promoLink}`
    }

    const customLink = () => {
        if (historyService?.history) {
            historyService.history.push(getPromoLink())

            let price = props.promoText.price

            if (typeof price === 'string') {
                price = parseFloat(price)
            }

            if (checkTrackingManager(trackingManager) && props.promoText) {
                setTracking(
                    trackingManager,
                    TrackingAction.VWBasic_VehiclePromotionsSelect_Click,
                    getVehiclePromotionsSelectClickData(
                        props.carName,
                        {
                            title: props.promoType,
                            price,
                        },
                        trackingBrand,
                        isShowroom
                    )
                )
            }
        }
    }

    return (
        <li>
            <SingleModelCard
                onClick={customLink}
                className={props.promoIsHighlighted ? HIGHLIGHTED_CLASS : ''}
            >
                <article>
                    <ModelFigure>
                        <ModelContainerShadow
                            style={!isPngImage ? {height: '100%'} : undefined}
                        />
                        <ModelHeader
                            $isPng={isPngImage}
                            $tagsWrapped={props.tagsWrapped}
                        >
                            {(props.tag_label_1 || props.tag_label_2) && (
                                <ModelTagsContainer data-tags-container>
                                    {props.tag_label_1 && (
                                        <ModelTag $isPrimary={true}>
                                            <StarOutline variant="small" />
                                            <Text
                                                tag={TextTag.span}
                                                appearance={
                                                    TokenTextAppearance.label150
                                                }
                                                color={TokenTextColor.tertiary}
                                            >
                                                {props.tag_label_1}
                                            </Text>
                                        </ModelTag>
                                    )}
                                    {props.tag_label_2 && (
                                        <ModelTag $isPrimary={false}>
                                            <Discount variant="small" />
                                            <Text
                                                tag={TextTag.span}
                                                appearance={
                                                    TokenTextAppearance.label150
                                                }
                                                color={TokenTextColor.tertiary}
                                            >
                                                {props.tag_label_2}
                                            </Text>
                                        </ModelTag>
                                    )}
                                </ModelTagsContainer>
                            )}
                            <ModelTitleRow
                                $abs={!isPngImage}
                                $hasTags={
                                    !!(props.tag_label_1 || props.tag_label_2)
                                }
                            >
                                <ModelName
                                    tag={TextTag.h2}
                                    bold={true}
                                    appearance={TokenTextAppearance.headline200}
                                    className={`${
                                        isPngImage
                                            ? 'text-black'
                                            : isTooDark(
                                                  props.backgroundColor
                                              ) || props.promoIsHighlighted
                                            ? 'text-white'
                                            : 'text-black'
                                    }`}
                                >
                                    {props.carName}
                                </ModelName>
                                {hasFuelTags && isFivePlusConfigured && (
                                    <TitleRowFuelTags
                                        fuels={electrifiedFuels}
                                    />
                                )}
                            </ModelTitleRow>
                            {hasFuelTags && !isFivePlusConfigured ? (
                                <ModelFuelTags fuels={electrifiedFuels} />
                            ) : undefined}
                            {isFivePlusConfigured ? (
                                <FivePlusLayerCta
                                    type="button"
                                    aria-label={fivePlusCtaLabel}
                                    onClick={(e: React.MouseEvent): void => {
                                        e.preventDefault()
                                        e.stopPropagation()

                                        void cmsService
                                            .openLayer?.(fivePlusLayerPath)
                                            .catch((error: unknown) => {
                                                console.error(
                                                    'Could not open 5 Plus layer',
                                                    error
                                                )
                                            })

                                        if (
                                            checkTrackingManager(
                                                trackingManager
                                            )
                                        ) {
                                            setTracking(
                                                trackingManager,
                                                TrackingAction.VWBasic_VehiclePromotionsLink_Click,
                                                getVehiclePromotionsLinkClickData(
                                                    fivePlusCtaLabel,
                                                    props.promoType,
                                                    props.carName,
                                                    props.promoLink,
                                                    trackingBrand,
                                                    isShowroom
                                                )
                                            )
                                        }
                                    }}
                                >
                                    <span>{fivePlusCtaLabel}</span>
                                    <Info aria-hidden={true} />
                                </FivePlusLayerCta>
                            ) : undefined}
                        </ModelHeader>

                        <ModelImageWithBackgroundContainer $isPng={isPngImage}>
                            <ModelImageContainer
                                $isPng={isPngImage}
                                $tagsWrapped={props.tagsWrapped}
                            >
                                <ModelImage
                                    $isPng={isPngImage}
                                    src={props.carImage}
                                    alt={props.carName}
                                />
                            </ModelImageContainer>
                        </ModelImageWithBackgroundContainer>
                    </ModelFigure>

                    <ContentContainer>
                        <ModelTextContainer
                            className={` ${
                                props.isDealerSelected &&
                                props.promoScope === PromotionType.LOCAL
                                    ? PROMO_AVAILABLE_CLASS
                                    : ''
                            }`}
                        >
                            {props.isDealerSelected &&
                                props.promoScope === PromotionType.LOCAL && (
                                    <DealerHighlighter
                                        dealerName={capitalizeWords(
                                            props.dealerName
                                        )}
                                    />
                                )}
                            {props.promoIsHighlighted ? (
                                <SingleModelEyeCatcherWrapper>
                                    <EyeCatcher
                                        appearance="recommended"
                                        text={t(props.promotional_equipment)}
                                        backgroundColor={
                                            ULTRAMARINE_EYE_CATCHER_BACKGROUND
                                        }
                                        contentColor={globalColors.white000}
                                    />
                                </SingleModelEyeCatcherWrapper>
                            ) : undefined}
                            {promoText ? (
                                <SingleModelPromoText
                                    dangerouslySetInnerHTML={{
                                        __html: promoText,
                                    }}
                                    className={`${
                                        props.promoIsHighlighted
                                            ? 'text-white'
                                            : ''
                                    } ${
                                        props.isDealerSelected &&
                                        props.isPromoAvailable
                                            ? PROMO_AVAILABLE_CLASS
                                            : ''
                                    }`}
                                />
                            ) : undefined}

                            <ModelPromoType
                                tag={TextTag.p}
                                className={
                                    props.isDealerSelected &&
                                    props.isPromoAvailable === 1 &&
                                    !props.promoText.promotional_package
                                        ? PROMO_AVAILABLE_CLASS
                                        : ''
                                }
                            >
                                {props.promoType}
                            </ModelPromoType>

                            {props.promoText.promotional_package &&
                            brandFromState === Brands.VW ? (
                                <EyeCatcherAccordionContainer
                                    className={
                                        props.isDealerSelected &&
                                        props.isPromoAvailable === 1
                                            ? PROMO_AVAILABLE_CLASS
                                            : ''
                                    }
                                >
                                    <EyeCatcher
                                        backgroundColor={
                                            ULTRAMARINE_EYE_CATCHER_BACKGROUND
                                        }
                                        contentColor={globalColors.white000}
                                        text={
                                            props.promoText.promotional_package
                                        }
                                    />
                                </EyeCatcherAccordionContainer>
                            ) : undefined}

                            {props.isDealerSelected &&
                                props.promoScope === PromotionType.NATIONAL &&
                                props.isPromoAvailable === 0 && (
                                    <EyeCatcherAccordionContainer>
                                        <EyeCatcher
                                            text={`${t(
                                                'selectedDealer.promoNotAvailable'
                                            )} <b>${capitalizeWords(
                                                props.dealerName
                                            )}</b>`}
                                            backgroundColor={
                                                PURPLE_EYE_CATCHER_BACKGROUND
                                            }
                                        />
                                    </EyeCatcherAccordionContainer>
                                )}
                        </ModelTextContainer>
                        <SingleModelCTA
                            tag="a"
                            emphasis="secondary"
                            size="small"
                            href={historyService.history.createHref(
                                getPromoLink()
                            )}
                            onClick={(e: React.MouseEvent): void => {
                                // navigation and tracking are handled by the
                                // card click handler
                                e.preventDefault()
                            }}
                            aria-label={t('cta.listing.seeAllPromo')}
                        >
                            {t('cta.listing.seeAllPromo')}
                        </SingleModelCTA>
                    </ContentContainer>
                </article>
            </SingleModelCard>
        </li>
    )
}
